import React, { FC, JSX } from 'react';
import { Typography } from '../Typography';
import styles from './Table.module.css';

type TableSubComponents = {
    Options: typeof Options;
};

export interface TableProps {
    id?: string;
    classes?: string;
    headerFontSize?: '12' | '14';
    headerCols?: {
        classes?: string;
        fit?: boolean;
        hideOnMobile?: boolean;
        hideOnDesktop?: boolean;
        text: string | JSX.Element;
    }[];
    hideHeaderOnMobile?: boolean;
    contentRows: { classes?: string; colspan?: number; content: string | JSX.Element | JSX.Element[] }[][];
    borderless?: boolean;
    compressedSize?: boolean;
}

export const Table: FC<TableProps> & TableSubComponents = ({
    id,
    classes,
    headerFontSize,
    headerCols,
    hideHeaderOnMobile,
    contentRows,
    borderless,
    compressedSize,
}: TableProps) => {
    return (
        <div className="table-responsive">
            <table
                id={id || undefined}
                className={
                    `table ${styles.table}` +
                    // small size
                    ` ${compressedSize ? `table-sm ${styles['table-sm']}` : ''}` +
                    // borders
                    ` ${borderless ? 'table-borderless' : ''}` +
                    // other classes
                    ` mb-0 ${classes || ''}`
                }
                data-testid="table">
                {headerCols && (
                    <thead>
                        <tr className={hideHeaderOnMobile ? 'd-none d-md-table-row' : ''}>
                            {headerCols.map((item, index) => (
                                <th
                                    key={`table-header-${index}`}
                                    className={
                                        `${item.classes || ''}` +
                                        ` ${item.fit ? styles.fit : ''}` +
                                        ` ${item.hideOnMobile ? styles['cell-hide-on-mobile'] : ''}` +
                                        ` ${item.hideOnDesktop ? styles['cell-hide-on-desktop'] : ''}`
                                    }>
                                    <Typography fontSize={headerFontSize || '14'}>{item.text}</Typography>
                                </th>
                            ))}
                        </tr>
                    </thead>
                )}
                <tbody>
                    {contentRows.map((row, rowIndex) => (
                        <tr key={`table-row-${rowIndex}`}>
                            {row.map((cell, cellIndex) => {
                                const hideOnMobile = headerCols && headerCols[cellIndex]?.hideOnMobile;
                                const hideOnDesktop = headerCols && headerCols[cellIndex]?.hideOnDesktop;
                                const isFit = headerCols && headerCols[cellIndex]?.fit;
                                return (
                                    <td
                                        key={`table-row-${rowIndex}-cell-${cellIndex}`}
                                        className={
                                            `${cell.classes || ''}` +
                                            ` ${isFit ? styles.fit : ''}` +
                                            ` ${hideOnMobile ? styles['cell-hide-on-mobile'] : ''}` +
                                            ` ${hideOnDesktop ? styles['cell-hide-on-desktop'] : ''}`
                                        }
                                        colSpan={cell.colspan || undefined}>
                                        {cell.content}
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

interface OptionsProps {
    classes?: string;
    options: JSX.Element[];
}

const Options: FC<OptionsProps> = ({ classes, options }) => {
    return (
        <div className={`${styles['table-options']} ${classes || ''}`}>
            {options.map((option, index) => (
                <div key={`option-${index}`} className={styles.option}>
                    {option}
                </div>
            ))}
        </div>
    );
};

Table.Options = Options;
